import { forwardRef, type ButtonHTMLAttributes } from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@rook/shared/lib/cn";

// shadcn/ui Button primitive — copy-paste with two ROOK adjustments:
// 1. `default` variant uses ROOK violet (`bg-rook hover:bg-rook/90`)
//    instead of shadcn's `bg-primary`. The `--primary` token in
//    globals.css points at the same hex, but for the brand CTA we
//    want the literal class so it's grep-able.
// 2. Sizes lean compact (CLAUDE.md: "compact button is the default;
//    hero is for the action").

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-rook text-white shadow-sm hover:bg-rook/90",
        secondary:
          "border border-white/15 bg-white/5 text-foreground hover:bg-white/10",
        ghost:
          "text-muted-foreground hover:bg-white/5 hover:text-foreground",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        link: "text-rook-light underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5 py-1.5 text-sm",
        sm: "h-8 px-3 text-xs",
        lg: "h-11 w-full px-6 text-sm font-semibold",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as a different element (Slot) — useful for `<Link>` styled as a button. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
